import React, { useState, useRef } from 'react';
import toastr from 'toastr';
import 'toastr/build/toastr.min.css';

import PrintSlip from './PrintSlip';
import ViewForm from './ViewForm';

const Search = () => {
  const [searchBox, setSearchBox] = useState('');
  const [receivedData, setReceivedData] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [applicationDetails, setApplicationDetails] = useState(null);

  const voucherOpen = useRef(null);
  const viewForm = useRef(null);

  const onChange = (e) => {
    const val = e.target.value.replace(/\D/g, "");
    setSearchBox(val);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (searchBox.length !== 13) {
      toastr.error("CNIC must be exactly 13 digits long");
      return;
    }
    setLoading(true);
    setHasSearched(false);
    setReceivedData([]);
    try {
      const response = await fetch(
        `https://etea.online/apply/api_coe/getCandidateInfobyCNIC1?cnic=${searchBox}`,
        { method: 'GET', headers: { 'Content-Type': 'application/json' } }
      );
      const json = await response.json();
      setHasSearched(true);
      if (json.Status) {
        if (!json.Message || json.Message.length === 0) {
          toastr.warning("Record not found");
        } else {
          setReceivedData(json.Message);
        }
      } else {
        toastr.error(json.Message || "No record exists for this CNIC");
      }
    } catch (error) {
      toastr.error('Error connecting to server.');
    } finally {
      setLoading(false);
    }
  };

  const styles = {
    mainWrapper: {
      flex: 1,
      padding: '40px 15px',
      maxWidth: '900px',
      width: '100%',
      margin: '0 auto',
      display: 'flex',
      flexDirection: 'column'
    },
    searchCard: {
      background: '#ffffff',
      padding: '30px',
      borderRadius: '20px',
      boxShadow: '0 10px 30px rgba(0,0,0,0.05)',
      border: '1px solid #f1f5f9',
      marginBottom: '30px'
    },
    statusBadge: (isActive) => ({
      padding: '6px 14px',
      borderRadius: '50px',
      fontSize: '12px',
      fontWeight: '700',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      backgroundColor: isActive ? '#dcfce7' : '#fee2e2',
      color: isActive ? '#166534' : '#991b1b',
      border: `1px solid ${isActive ? '#bbf7d0' : '#fecaca'}`,
      marginRight: '8px',
      marginBottom: '8px'
    }),
    noRecordBox: {
      background: '#fff5f5',
      border: '1px solid #feb2b2',
      borderRadius: '15px',
      padding: '40px',
      textAlign: 'center',
      marginTop: '20px'
    }
  };

  return (
    <div style={styles.mainWrapper}>
      <style>
        {`
          .page-title { color: #1e293b; font-weight: 800; font-size: 1.8rem; text-align: center; margin-bottom: 5px; }
          .page-subtitle { color: #64748b; text-align: center; margin-bottom: 35px; font-size: 1rem; }
          .search-input { 
             height: 50px; border-radius: 12px; font-size: 1.1rem; 
             border: 2px solid #e2e8f0; transition: 0.3s; padding: 0 15px;
          }
          .search-input:focus { border-color: #3b82f6; box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1); outline: none; }
          .btn-search { 
             height: 50px; border-radius: 12px; font-weight: 700; 
             min-width: 160px; transition: 0.3s; 
          }
          .urdu-label { font-family: 'Tahoma'; direction: rtl; display: block; font-size: 13px; color: #64748b; margin-top: 5px; }
          .result-item { 
             background: white; border-radius: 16px; padding: 25px; 
             border: 1px solid #f1f5f9; margin-top: 15px; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05);
          }
          .instruction-banner { 
             background-color: #f0f9ff; border-left: 5px solid #3b82f6; 
             padding: 20px; border-radius: 12px; margin-bottom: 25px; color: #0c4a6e;
          }
          @media (max-width: 600px) {
            .flex-mobile { flex-direction: column; gap: 10px !important; }
            .btn-search { width: 100%; }
          }
        `}
      </style>

      <h2 className="page-title">Check Application Status</h2>
      <p className="page-subtitle">Centers of Excellence Scholarship Program</p>

      <div style={styles.searchCard}>
        <form onSubmit={handleSubmit}>
          <label className="fw-bold mb-2" style={{ color: '#334155' }}>Enter CNIC (13 Digits):</label>

          {/* GAP INCREASED TO 25px FOR MORE PADDING BETWEEN INPUT AND BUTTON */}
          <div className="flex-mobile" style={{ display: 'flex', gap: '25px', alignItems: 'flex-start' }}>
            <div style={{ flex: '1' }}>
              <input
                type="text"
                maxLength={13}
                value={searchBox}
                onChange={onChange}
                className="form-control search-input"
                placeholder="1730100000000"
                disabled={loading}
              />
              <span className="urdu-label">شناختی کارڈ نمبر درج کریں (بغیر ڈیش کے)</span>
            </div>

            <button
              type="submit"
              className="btn btn-primary btn-search"
              disabled={loading || searchBox.length < 13}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Loading...
                </>
              ) : (
                "Search Now"
              )}
            </button>
          </div>
        </form>
      </div>

      {hasSearched && receivedData.length === 0 && !loading && (
        <div style={styles.noRecordBox}>
          <div style={{ fontSize: '50px', marginBottom: '15px' }}>🔎</div>
          <h4 style={{ color: '#991b1b', fontWeight: '800' }}>No Record Found!</h4>
          <p className="mb-1 text-muted">No application found for CNIC: <b>{searchBox}</b></p>
          <span className="urdu-label" style={{ textAlign: 'center', color: '#b91c1c' }}>
            درج کردہ شناختی کارڈ پر کوئی ریکارڈ نہیں ملا۔
          </span>
        </div>
      )}

      {receivedData.length > 0 && (
        <div className="results-section">
          <div className="instruction-banner">
            <i className="fa fa-info-circle me-2"></i>
            <strong>Instructions:</strong> Use ETEA App to upload photo if <b>Photo Status</b> is ❌.
            <span className="urdu-label" style={{ textAlign: 'left', color: '#0369a1' }}>اگر تصویر کے سامنے ❌ ہے تو ایٹا ایپ سے اپلوڈ کریں۔</span>
          </div>

          {receivedData.map((data, index) => (
            <div className="result-item" key={index}>
              <div className="row align-items-center">
                <div className="col-md-8">
                  <h5 style={{ fontWeight: '800', color: '#1e293b', marginBottom: '8px' }}>{data.name}</h5>
                  <p className="text-muted mb-3"><i className="fa fa-id-card me-2"></i>CNIC: {data.cnic}</p>

                  <div className="d-flex flex-wrap">
                    <span style={styles.statusBadge(true)}>Application Submitted ✅</span>
                    <span style={styles.statusBadge(data.photo !== "")}>Photo {data.photo !== "" ? "✅" : "❌"}</span>
                    <span style={styles.statusBadge(data.payment_status !== 0)}>Payment {data.payment_status !== 0 ? "✅" : "❌"}</span>
                  </div>
                </div>

                <div className="col-md-4 text-md-end mt-3 mt-md-0">
                  {data.payment_status === 0 && data.photo !== "" ? (
                    <button className="btn btn-success w-100 py-2 fw-bold" onClick={() => { setApplicationDetails(data); voucherOpen.current.click(); }}>
                      🖨️ Print Voucher
                    </button>
                  ) : (
                    <button className="btn btn-info w-100 py-2 text-white fw-bold" onClick={() => { setApplicationDetails(data); viewForm.current.click(); }}>
                      👁️ View Form
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <button style={{ display: 'none' }} ref={voucherOpen} data-toggle="modal" data-target="#pdfhandler"></button>
      <button style={{ display: 'none' }} ref={viewForm} data-toggle="modal" data-target="#viewForm"></button>

      <PrintSlip applicationDetails={applicationDetails} />
      <ViewForm applicationDetails={applicationDetails} />
    </div>
  );
}

export default Search;